import type { AdvisedProduct, Integration, UnionOfType } from "../../../../app/types"
import type { Product } from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
} from "../types"

type GoogleTagManagerBase<T> = {
  type: T
  integration: "aiden:google_tag_manager"
  advisorId: string
  advisorName: string
}

type GoogleTagManagerAnsweredEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_answered_question"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_answered_question",
      aiden_app_id: string
      aiden_app_name: string
      aiden_question: string
      answers: string[]
    }
  }
>

type GoogleTagManagerAnswerGivenEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_answer_given"> & {
  eventData: {
    event: "aiden_event",
    aiden_event: "aiden_answer_given",
    aiden_app_id: string
    aiden_app_name: string
    aiden_question: string
    aiden_answer: string
  }
}
>

type GoogleTagManagerAdvisedEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_advised"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_advised",
      aiden_app_id: string
      aiden_app_name: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type GoogleTagManagerProductAdvisedEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_product_advised"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_product_advised",
      aiden_app_id: string
      aiden_app_name: string
      aiden_product_id: string
      aiden_product: string
      aiden_product_image: string
      aiden_product_link: string
    }
  }
>

type GoogleTagManagerAdvicePageCustomerInfoEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_advice_page_customer_info_form"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_advice_page_customer_info_form",
      aiden_app_id: string
      aiden_app_name: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
      advice: { id: string; name: string; image: string; link: string }[]
    }
  }
>

type GoogleTagManagerNoResultsPageCustomerInfoEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_no_results_page_customer_info_form"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_no_results_page_customer_info_form",
      aiden_app_id: string
      aiden_app_name: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
    }
  }
>

type GoogleTagManagerInfoPageCustomerInfoEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_info_page_customer_info_form"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_info_page_customer_info_form",
      aiden_app_id: string
      aiden_app_name: string
      email: string
      checkboxChecked: boolean
      checkboxContext: string
      formIdentifier: string
    }
  }
>

type GoogleTagManagerProductClickedEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_product_clicked"> & {
    eventData: {
      event: "aiden_event",
      aiden_event: "aiden_product_clicked",
      aiden_app_id: string
      aiden_app_name: string

      product: {
        id: string
        name: string
        link: string
      }
    }
  }
>

type GoogleTagManagerConversationStartedEvent = UnionOfType<
  GoogleTagManagerBase<"aiden_conversation_started"> & {
  eventData: {
    event: "aiden_event",
    aiden_event: "aiden_conversation_started",
    aiden_app_id: string
    aiden_app_name: string
  }
}
>


export const GoogleTagManagerUsageEvents: UsageAnalyticsEventTransformer = {
  questionAnswered: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    question: { title: string; label: string },
    answers: string[]
  ): (GoogleTagManagerAnsweredEvent | GoogleTagManagerAnswerGivenEvent)[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_answered_question",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_answered_question",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          aiden_question: question.title,
          answers: answers,
        },
      },
      ...answers.map((answer) => {
        const event: GoogleTagManagerAnswerGivenEvent = {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:google_tag_manager",
          type: "aiden_answer_given",
          eventData: {
            event: "aiden_event",
            aiden_event: "aiden_answer_given",
            aiden_app_id: advisor.id,
            aiden_app_name: advisor.name,
            aiden_question: question.title,
            aiden_answer: answer,
          },
        }
        return event
      }),
    ]
  },

  adviceGiven: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    products: Product[]
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  ): (GoogleTagManagerAdvisedEvent | GoogleTagManagerProductAdvisedEvent)[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_advised",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_advised",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          advice: products.map((p) => ({
            id: p.clientProductId,
            name: p.name,
            image: p.image,
            link: p.link,
          })),
        },
      },
      ...products.map((product) => {
        const event: GoogleTagManagerProductAdvisedEvent = {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:google_tag_manager",
          type: "aiden_product_advised",
          eventData: {
            event: "aiden_event",
            aiden_event: "aiden_product_advised",
            aiden_app_id: advisor.id,
            aiden_app_name: advisor.name,
            aiden_product_id: product.clientProductId,
            aiden_product: product.name,
            aiden_product_image: product.image,
            aiden_product_link: product.link,
          },
        }
        return event
      }),
    ]
  },

  clickedOnProduct: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    product: Product
  ): GoogleTagManagerProductClickedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_product_clicked",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_product_clicked",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          product: {
            id: product.clientProductId,
            name: product.name,
            link: product.link,
          },
        },
      },
    ]
  },

  customerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      advisedProducts: AdvisedProduct[]
    }
  ): GoogleTagManagerAdvicePageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_advice_page_customer_info_form",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_advice_page_customer_info_form",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          advice: info.advisedProducts.map((p) => ({
            id: p.id,
            name: p.name,
            image: p.imageUrl,
            link: p.url,
          })),
        },
      },
    ]
  },

  infoPageCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
      formIdentifier: string
    }
  ): GoogleTagManagerInfoPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_info_page_customer_info_form",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_info_page_customer_info_form",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
          formIdentifier: info.formIdentifier,
        },
      },
    ]
  },

  noResultsCustomerInfoSubmitted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
    isPreview: boolean,
    info: {
      email: string
      checkboxChecked: boolean
      checkboxText: string
    }
  ): GoogleTagManagerNoResultsPageCustomerInfoEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_no_results_page_customer_info_form",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_no_results_page_customer_info_form",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
          email: normalizeEmail(info.email),
          checkboxChecked: info.checkboxChecked,
          checkboxContext: info.checkboxText,
        },
      },
    ]
  },

  conversationStarted: (
    advisor: AdvisorInfo,
    context: SessionContext,
    integrations: Integration[],
  ): GoogleTagManagerConversationStartedEvent[] => {
    return [
      {
        advisorId: advisor.id,
        advisorName: advisor.name,
        integration: "aiden:google_tag_manager",
        type: "aiden_conversation_started",
        eventData: {
          event: "aiden_event",
          aiden_event: "aiden_conversation_started",
          aiden_app_id: advisor.id,
          aiden_app_name: advisor.name,
        },
      },
    ]
  },
}

function normalizeEmail(email: string): string {
  return email.toLowerCase().trim()
}
